import { useEffect, useState, type FormEvent } from 'react';
import { authErrorMessage, useAuth } from '../auth/AuthProvider';
import { AppLink, useGo } from '../components/Nav';
import { Banner, Button, Input, Spinner } from '../components/ui';
import { errorMessage } from '../lib/api';
import { firebaseConfigured } from '../lib/env';
import { PASSWORD_MIN, passwordProblem } from '../lib/password';
import { AuthFrame } from './AuthPages';

/**
 * The two halves of a forgotten password: /forgot-password asks for the
 * address, /reset-password is where the link in the email lands.
 *
 * Both are pages rather than states of /login, because both are errands of
 * their own: one ends in "check your inbox" and the other ends signed in, and
 * neither reads as a sign-in form that happens to have gone sideways.
 */

/** The address to start with, from whatever was typed on the sign-in form. */
function emailFromQuery(): string {
  return new URLSearchParams(window.location.search).get('email') ?? '';
}

export function ForgotPasswordPage() {
  const { sendReset } = useAuth();

  const [email, setEmail] = useState(emailFromQuery);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const address = email.trim();
    sendReset(address)
      // Sent, or not sent because nobody has that address: the page cannot
      // tell, and says the same thing either way. The API answers the same
      // way for the same reason.
      .then(() => setSent(address))
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  if (sent) {
    return (
      <AuthFrame
        title="Check your email"
        subtitle={`If there is a Slate account for ${sent}, a link to set a new password is on its way.`}
        footer={
          <>
            Nothing in a few minutes? Look in spam, or{' '}
            <button
              type="button"
              onClick={() => setSent(null)}
              className="text-signal hover:underline"
            >
              try another address
            </button>
            .
          </>
        }
      >
        <Banner tone="info" title="The link works once">
          It expires in an hour. Asking again sends a fresh one and retires the old.
        </Banner>
        <AppLink to="/login" className="block w-full">
          <Button variant="secondary" size="lg" className="w-full">
            Back to sign in
          </Button>
        </AppLink>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Forgot your password?"
      subtitle="Give us the address on your account and we will send a link to set a new one."
      footer={
        <>
          Remembered it?{' '}
          <AppLink to="/login" className="text-signal hover:underline">
            Sign in
          </AppLink>
          .
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        {error ? <Banner tone="danger">{error}</Banner> : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!firebaseConfigured}
        >
          Send the link
        </Button>
      </form>
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const { verifyReset, completeReset } = useAuth();
  const go = useGo();

  const code = new URLSearchParams(window.location.search).get('oobCode') ?? '';
  // Whose account the link is for. Checked before the form is shown, so a
  // stale link is a sentence about the link rather than a rejection after
  // somebody has chosen a password.
  const [account, setAccount] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [dead, setDead] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) {
      setDead('This link is missing its code. Use the button in the email, or ask for a new link.');
      setChecking(false);
      return;
    }
    let live = true;
    verifyReset(code)
      .then((address) => {
        if (live) setAccount(address);
      })
      .catch((cause: unknown) => {
        if (live) setDead(authErrorMessage(cause));
      })
      .finally(() => {
        if (live) setChecking(false);
      });
    return () => {
      live = false;
    };
  }, [code, verifyReset]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const problem = passwordProblem(password, account ?? '');
    if (problem) {
      setError(problem);
      return;
    }
    if (password !== confirm) {
      setError('Those two do not match.');
      return;
    }

    setBusy(true);
    completeReset(code, password)
      // Signed in by completeReset, so this is the library, not /login.
      .then(() => go('/library', { replace: true }))
      .catch((cause: unknown) => {
        setError(authErrorMessage(cause));
        setBusy(false);
      });
  };

  if (checking) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  if (dead) {
    return (
      <AuthFrame
        title="That link is no good"
        subtitle="Reset links work once and expire after an hour."
        footer={
          <>
            Know your password after all?{' '}
            <AppLink to="/login" className="text-signal hover:underline">
              Sign in
            </AppLink>
            .
          </>
        }
      >
        <Banner tone="danger">{dead}</Banner>
        <AppLink to="/forgot-password" className="block w-full">
          <Button variant="primary" size="lg" className="w-full">
            Send a new link
          </Button>
        </AppLink>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title="Set a new password"
      subtitle={`For ${account}. You will be signed in as soon as it is saved.`}
      footer={
        <>
          Not your account?{' '}
          <AppLink to="/login" className="text-signal hover:underline">
            Sign in as someone else
          </AppLink>
          .
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {/* Off screen, but there for password managers: they file the new
            password against the account it belongs to. */}
        <input type="email" autoComplete="username" value={account ?? ''} readOnly hidden />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={`At least ${PASSWORD_MIN} characters. A phrase works well.`}
          minLength={PASSWORD_MIN}
          required
        />
        <Input
          label="Again, to be sure"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          required
        />

        {error ? <Banner tone="danger">{error}</Banner> : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!firebaseConfigured}
        >
          Save and sign in
        </Button>
      </form>
    </AuthFrame>
  );
}
